import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { collection, query, orderBy, onSnapshot, limit, doc } from 'firebase/firestore';

interface HeaderProps {
    onMenuClick: () => void;
    title: string;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, title }) => {
    const navigate = useNavigate();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);
    
    // Read state from localStorage
    const [readIds, setReadIds] = useState<string[]>(() => {
        try {
            return JSON.parse(localStorage.getItem('read_notifications') || '[]');
        } catch {
            return [];
        }
    });

    // Listen to notifications module access in real-time
    useEffect(() => {
        const docRef = doc(db, 'tenantSettings', 'modules');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const config = data['Notifications'];
                if (config === undefined || config === null) {
                    setIsNotificationEnabled(false);
                } else if (typeof config === 'boolean') {
                    setIsNotificationEnabled(config);
                } else if (typeof config === 'object' && config !== null) {
                    setIsNotificationEnabled(config.enabled === true);
                } else {
                    setIsNotificationEnabled(false);
                }
            } else {
                setIsNotificationEnabled(false);
            }
        }, (err) => {
            console.error('Error fetching notifications module status in Header:', err);
            setIsNotificationEnabled(false);
        });
        return () => unsubscribe();
    }, []);

    const { user, signOut } = useAuth();
    const userMenuRef = useRef<HTMLDivElement>(null);
    const notificationsRef = useRef<HTMLDivElement>(null);

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
            if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Sync read notifications whenever dropdown is shown
    useEffect(() => {
        if (showNotifications) {
            try {
                setReadIds(JSON.parse(localStorage.getItem('read_notifications') || '[]'));
            } catch {
                setReadIds([]);
            }
        }
    }, [showNotifications]);

    // Listen to real-time notifications
    useEffect(() => {
        const q = query(
            collection(db, 'notifications'),
            orderBy('createdAt', 'desc'),
            limit(10)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: any[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() });
            });
            setNotifications(list);
        }, (error) => {
            console.error('Error listening to notifications in Header:', error);
        });
        return () => unsubscribe();
    }, []);

    const isSuperAdmin = user?.role === 'super_admin';

    const unreadNotifications = notifications.filter(n => !readIds.includes(n.id));
    const hasUnread = unreadNotifications.length > 0;

    const handleMarkAllRead = () => {
        const allIds = notifications.map(n => n.id);
        setReadIds(allIds);
        localStorage.setItem('read_notifications', JSON.stringify(allIds));
    };

    return (
        <header className={`${
            isSuperAdmin 
                ? 'bg-slate-950/80 border-b border-emerald-500/20 text-slate-100 shadow-[0_4px_30px_rgba(0,0,0,0.5)]' 
                : 'bg-white/90 border-b border-white/20 text-gray-900 shadow-lg'
        } backdrop-blur-md h-16 flex items-center justify-between px-4 lg:px-6 flex-shrink-0 relative z-[45]`}>
            <div className="flex items-center">
                <button
                    onClick={onMenuClick}
                    className={`lg:hidden p-2 rounded-xl mr-3 transition-all duration-200 ${
                        isSuperAdmin 
                            ? 'text-slate-400 hover:text-emerald-400 hover:bg-slate-900' 
                            : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                    }`}
                >
                    <Menu className="h-5 w-5" />
                </button>
                <div>
                    <h1 className={`text-xl font-bold ${
                        isSuperAdmin ? 'text-emerald-400 font-extrabold tracking-wide' : 'gradient-text'
                    }`}>{title}</h1>
                    <p className={`text-xs ${
                        isSuperAdmin ? 'text-slate-400 font-semibold' : 'text-gray-500'
                    }`}>Noor-e-Hamza Islamic College</p>
                </div>
            </div>

            <div className="flex items-center space-x-3">
                {/* Notifications */}
                {isNotificationEnabled && (
                    <div className="relative" ref={notificationsRef}>
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className={`p-2.5 rounded-xl relative transition-all duration-200 group ${
                                isSuperAdmin 
                                    ? 'text-slate-400 hover:text-emerald-400 hover:bg-slate-900' 
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                            }`}
                        >
                            <Bell className="h-5 w-5" />
                            {hasUnread && (
                                <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-gradient-to-r from-red-500 to-red-600 rounded-full animate-pulse"></span>
                            )}
                        </button>

                        {/* Notifications Dropdown Menu */}
                        {showNotifications && (
                            <div className={`absolute right-0 mt-2 w-80 rounded-xl shadow-2xl py-2 z-[99999] border ${
                                isSuperAdmin 
                                    ? 'bg-slate-950 border-emerald-500/20 text-slate-200 shadow-[0_10px_30px_rgba(0,0,0,0.5)]' 
                                    : 'bg-white border-gray-200 text-gray-800'
                            }`}>
                                <div className={`px-4 py-2 border-b flex items-center justify-between ${
                                    isSuperAdmin ? 'border-emerald-500/10' : 'border-gray-100'
                                }`}>
                                    <span className="font-bold text-sm">Notifications</span>
                                    {hasUnread && (
                                        <button 
                                            onClick={handleMarkAllRead}
                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                                        >
                                            Mark all as read
                                        </button>
                                    )}
                                </div>

                                <div className="max-h-64 overflow-y-auto divide-y divide-gray-50 custom-scrollbar text-xs">
                                    {notifications.length === 0 ? (
                                        <div className="p-4 text-center text-gray-400">
                                            No notifications yet
                                        </div>
                                    ) : (
                                        notifications.map((n) => {
                                            const typeColor = 
                                                n.type === 'warning' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                                                n.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                                                'bg-blue-50 text-blue-600 border border-blue-100';
                                            
                                            const isUnread = !readIds.includes(n.id);
                                            
                                            return (
                                                <div key={n.id} className={`p-3 hover:bg-slate-50 transition-colors flex gap-2.5 items-start text-left ${isUnread ? 'bg-slate-50/30' : ''}`}>
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${typeColor}`}>
                                                        <Bell className="h-4 w-4" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-1.5">
                                                            <p className="font-semibold text-gray-900 truncate">{n.title}</p>
                                                            {isUnread && (
                                                                <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full shrink-0"></span>
                                                            )}
                                                        </div>
                                                        <p className="text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{n.description || n.message}</p>
                                                        <span className="text-[10px] text-gray-400 block mt-1">
                                                            {n.createdAt ? new Date(n.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                <div className={`px-4 py-1.5 border-t text-center ${
                                    isSuperAdmin ? 'border-emerald-500/10' : 'border-gray-100'
                                }`}>
                                    <button
                                        onClick={() => {
                                            setShowNotifications(false);
                                            navigate('/notifications');
                                        }}
                                        className="text-xs text-gray-500 hover:text-emerald-600 font-semibold transition-colors w-full text-center py-1"
                                    >
                                        View All Notifications
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                    <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className={`flex items-center space-x-2 p-2 rounded-xl transition-all duration-200 group ${
                            isSuperAdmin ? 'hover:bg-slate-900' : 'hover:bg-white/50'
                        }`}
                    >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isSuperAdmin 
                                ? 'bg-gradient-to-br from-emerald-50 to-teal-100 border border-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.2)]' 
                                : 'bg-gradient-to-r from-emerald-500 to-teal-600'
                        }`}>
                            <User className={`h-4 w-4 ${isSuperAdmin ? 'text-emerald-600' : 'text-white'}`} />
                        </div>
                        <div className="hidden md:block text-left">
                            <p className={`text-sm font-medium ${
                                isSuperAdmin ? 'text-slate-200 font-bold' : 'text-gray-900'
                             }`}>{user?.displayName || 'Admin'}</p>
                            <p className={`text-xs ${
                                isSuperAdmin ? 'text-emerald-400 font-extrabold uppercase tracking-wider text-[9px]' : 'text-gray-500 capitalize'
                            }`}>{user?.role || 'Admin'}</p>
                        </div>
                    </button>

                    {/* Dropdown Menu */}
                    {showUserMenu && (
                        <div className={`absolute right-0 mt-2 w-48 rounded-xl shadow-2xl py-2 z-[99999] border ${
                            isSuperAdmin 
                                ? 'bg-slate-950 border-emerald-500/20 text-slate-200 shadow-[0_10px_30px_rgba(0,0,0,0.5)]' 
                                : 'bg-white border-gray-200 text-gray-800'
                        }`}>
                            <div className={`px-4 py-2 border-b ${
                                isSuperAdmin ? 'border-emerald-500/10' : 'border-gray-100'
                            }`}>
                                <p className={`text-sm font-bold ${
                                    isSuperAdmin ? 'text-slate-200' : 'text-gray-900'
                                }`}>{user?.displayName || 'Admin'}</p>
                                <p className={`text-xs ${
                                    isSuperAdmin ? 'text-slate-400' : 'text-gray-500'
                                }`}>{user?.email}</p>
                            </div>
                            <button className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 transition-colors ${
                                isSuperAdmin 
                                    ? 'text-slate-300 hover:bg-slate-900 hover:text-emerald-400' 
                                    : 'text-gray-700 hover:bg-slate-50'
                            }`}>
                                <Settings className="h-4 w-4 text-gray-400" />
                                <span>Settings</span>
                            </button>
                            <button
                                onClick={handleSignOut}
                                className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 transition-colors ${
                                    isSuperAdmin 
                                        ? 'text-red-400 hover:bg-red-950/20 hover:text-red-300' 
                                        : 'text-red-600 hover:bg-red-50'
                                }`}
                            >
                                <LogOut className="h-4 w-4" />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
